import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Checkbox from '@/components/ui/Checkbox';
import useTranslation from '@/hooks/useTranslation';
import { fetchPublicHiringLocations, submitPublicJobApplication } from '@/lib/api/publicHiring';

const slugify = (value, fallback = 'open-role') => {
  if (!value) return fallback;
  const slug = value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

const defaultForm = {
  fullName: '',
  email: '',
  phone: '',
  positionTitle: '',
  positionId: '',
  locationId: '',
  experienceSummary: '',
  coverLetter: '',
  availableFrom: '',
  preferredContactMethod: 'email',
  resumeUrl: '',
  consentToContact: true,
};

const PublicJobApplicationPage = () => {
  const { t } = useTranslation(['hiring']);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPublicHiringLocations();
      setLocations(data);
      if (!form.locationId && data?.[0]?.id) {
        setForm((prev) => ({ ...prev, locationId: data[0].id }));
      }
      setError('');
    } catch (err) {
      setError(
        t('hiring.publicForm.loadError', 'We could not load locations right now. Please retry.'),
      );
    } finally {
      setLoading(false);
    }
  }, [form.locationId, t]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const selectedLocation = useMemo(
    () => locations.find((location) => location?.id === form.locationId) ?? null,
    [form.locationId, locations],
  );

  const positionOptions = useMemo(() => {
    if (!selectedLocation?.suggestedPositions?.length) {
      return [];
    }
    return selectedLocation.suggestedPositions.map((position) => ({
      value: slugify(position),
      label: position,
    }));
  }, [selectedLocation]);

  const handleChange = (field) => (eventOrValue) => {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckbox = (field) => (event) => {
    const value = event?.target?.checked ?? false;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.locationId) {
      setError(t('hiring.publicForm.loadError', 'Please pick a location to continue.'));
      return;
    }
    setSubmitting(true);
    setSuccessMessage('');

    const positionId = form.positionId || slugify(form.positionTitle);
    const selectedPositionLabel =
      positionOptions.find((option) => option.value === form.positionId)?.label || form.positionTitle;

    try {
      const payload = {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone || undefined,
        positionTitle: selectedPositionLabel || form.positionId,
        locationName: selectedLocation?.name,
        experienceSummary: form.experienceSummary || undefined,
        coverLetter: form.coverLetter || undefined,
        availableFrom: form.availableFrom || undefined,
        preferredContactMethod: form.preferredContactMethod || undefined,
        resumeUrl: form.resumeUrl || undefined,
        consentToContact: form.consentToContact,
      };

      const response = await submitPublicJobApplication(form.locationId, positionId, payload);
      const applicationNumber = response?.applicationNumber ?? positionId;
      setSuccessMessage(
        t(
          'hiring.publicForm.success',
          'Thanks! Your application has been received and queued for review.',
        ) +
          (applicationNumber ? ` (${applicationNumber})` : ''),
      );
      setForm({ ...defaultForm, locationId: form.locationId });
      setError('');
    } catch (err) {
      setError(
        t(
          'hiring.publicForm.submitError',
          'We could not submit your application. Please check your details and try again.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/50 to-background">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
        <div className="bg-card border border-border shadow-elevation-1 rounded-2xl p-6 sm:p-10 space-y-6">
          <div className="space-y-2 text-center">
            <p className="text-sm uppercase tracking-wide text-muted-foreground">
              {t('hiring.publicForm.subtitle', 'Join our team')}
            </p>
            <h1 className="text-3xl font-bold text-foreground">{t('hiring.publicForm.title', 'Apply for a role')}</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t(
                'hiring.publicForm.description',
                'Tell us a little about yourself and we will get back to you as soon as possible.',
              )}
            </p>
          </div>

          {error ? (
            <div className="rounded-lg border border-error/30 bg-error/10 text-error px-4 py-3 text-sm">
              {error}
            </div>
          ) : null}

          {successMessage ? (
            <div className="rounded-lg border border-success/30 bg-success/10 text-success px-4 py-3 text-sm">
              {successMessage}
            </div>
          ) : null}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('hiring.publicForm.fullName', 'Full name')}
                required
                value={form.fullName}
                onChange={handleChange('fullName')}
                placeholder={t('hiring.publicForm.fullNamePlaceholder', 'Alex Rivera')}
              />
              <Input
                label={t('hiring.publicForm.email', 'Email')}
                type="email"
                required
                value={form.email}
                onChange={handleChange('email')}
                placeholder="alex@example.com"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('hiring.publicForm.phone', 'Phone (optional)')}
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder={t('hiring.publicForm.phonePlaceholder', '+1 555 123 4567')}
              />
              <Input
                label={t('hiring.publicForm.availableFrom', 'Earliest start date')}
                type="date"
                value={form.availableFrom}
                onChange={handleChange('availableFrom')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label={t('hiring.publicForm.location', 'Preferred location')}
                options={locations.map((location) => ({ value: location.id, label: location.name }))}
                value={form.locationId}
                onChange={handleChange('locationId')}
                loading={loading}
                required
              />
              <Select
                label={t('hiring.publicForm.position', 'Position')}
                options={positionOptions}
                value={form.positionId}
                onChange={handleChange('positionId')}
                placeholder={t('hiring.publicForm.positionPlaceholder', 'Select a position')}
              />
            </div>

            <Input
              label={t('hiring.publicForm.positionTitle', 'Position title (if not listed)')}
              value={form.positionTitle}
              onChange={handleChange('positionTitle')}
              placeholder={t('hiring.publicForm.positionTitlePlaceholder', 'Line Cook')}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('hiring.publicForm.resumeUrl', 'Resume URL (optional)')}
                value={form.resumeUrl}
                onChange={handleChange('resumeUrl')}
                placeholder={t('hiring.publicForm.resumePlaceholder', 'Link to resume or portfolio')}
              />
              <Input
                label={t('hiring.publicForm.preferredContact', 'Preferred contact method')}
                value={form.preferredContactMethod}
                onChange={handleChange('preferredContactMethod')}
                placeholder={t('hiring.publicForm.preferredContactPlaceholder', 'Email or phone')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  {t('hiring.publicForm.experience', 'Experience summary')}
                </label>
                <textarea
                  className="min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.experienceSummary}
                  onChange={handleChange('experienceSummary')}
                  placeholder={t('hiring.publicForm.experiencePlaceholder', 'Share a few highlights of your background...')}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  {t('hiring.publicForm.coverLetter', 'Cover letter')}
                </label>
                <textarea
                  className="min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.coverLetter}
                  onChange={handleChange('coverLetter')}
                  placeholder={t('hiring.publicForm.coverLetterPlaceholder', 'Why do you want to join us?')}
                />
              </div>
            </div>

            <Checkbox
              label={t('hiring.publicForm.consent', 'I agree to be contacted about my application')}
              checked={form.consentToContact}
              onChange={handleCheckbox('consentToContact')}
            />

            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-muted-foreground">
                {t('hiring.publicForm.disclaimer', 'We respect your privacy and will only use your details for hiring purposes.')}
              </p>
              <Button type="submit" variant="default" disabled={submitting || loading}>
                {submitting
                  ? t('hiring.publicForm.submitting', 'Submitting...')
                  : t('hiring.publicForm.submit', 'Submit application')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicJobApplicationPage;
